import { useEffect, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Plugin OAuth authentication page (AC1, Spec 003)
 *
 * This page is opened in the user's browser when they click "Sign In"
 * in the plugin. It handles the OAuth flow and redirects back to the
 * plugin's LocalAuthServer with the authorization code.
 *
 * URL format: /auth/plugin?state={token}&redirect={localhost_url}&productId={id}
 */
const PluginAuthPage = () => {
  const { theme } = useTheme();
  const [status, setStatus] = useState<'loading' | 'authenticating' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [productName, setProductName] = useState<string>('plugin');

  useEffect(() => {
    handlePluginAuth();
  }, []);

  const handlePluginAuth = async () => {
    try {
      // Parse URL parameters
      const params = new URLSearchParams(window.location.search);
      const state = params.get('state');
      const redirect = params.get('redirect');
      const productId = params.get('productId');

      if (!state || !redirect) {
        setStatus('error');
        setErrorMessage('Missing required parameters. Please try signing in from the plugin again.');
        return;
      }

      // Set product name for display
      if (productId) {
        setProductName(productId.charAt(0).toUpperCase() + productId.slice(1));
      }

      setStatus('authenticating');

      // In a real implementation, this would redirect to WorkOS for authentication
      // For now, we'll simulate the flow

      // Check if user is already authenticated (via session/cookie)
      const isAuthenticated = checkUserAuthentication();

      if (isAuthenticated) {
        // User is authenticated, get auth code and redirect back to plugin
        const authCode = await getAuthorizationCode();

        // Redirect back to plugin's localhost server
        const redirectUrl = new URL(redirect);
        redirectUrl.searchParams.set('code', authCode);
        redirectUrl.searchParams.set('state', state);

        setStatus('success');

        // Small delay to show success message before redirect
        setTimeout(() => {
          window.location.href = redirectUrl.toString();
        }, 1500);
      } else {
        // Need to authenticate - redirect to WorkOS
        // Store state and redirect in session for callback
        sessionStorage.setItem('plugin_auth_state', state);
        sessionStorage.setItem('plugin_auth_redirect', redirect);
        sessionStorage.setItem('plugin_auth_product', productId || 'marketplace');

        // Redirect to WorkOS (or show login form for mock auth)
        // For development, we'll show a simplified login
        setStatus('authenticating');
      }
    } catch (error) {
      console.error('Plugin auth error:', error);
      setStatus('error');
      setErrorMessage('Authentication failed. Please try again.');
    }
  };

  // Mock function - replace with actual session check
  const checkUserAuthentication = (): boolean => {
    // Check if user has an active session
    return localStorage.getItem('marketplace_auth_token') !== null;
  };

  // Mock function - replace with actual OAuth flow
  const getAuthorizationCode = async (): Promise<string> => {
    // In production, this would be returned from WorkOS callback
    // For now, generate a mock code
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const handleMockSignIn = async () => {
    setStatus('authenticating');

    try {
      // Simulate authentication delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Store mock token
      localStorage.setItem('marketplace_auth_token', 'mock_token_' + Date.now());

      // Get original parameters
      const params = new URLSearchParams(window.location.search);
      const state = params.get('state');
      const redirect = params.get('redirect');

      if (state && redirect) {
        const authCode = await getAuthorizationCode();
        const redirectUrl = new URL(redirect);
        redirectUrl.searchParams.set('code', authCode);
        redirectUrl.searchParams.set('state', state);

        setStatus('success');

        setTimeout(() => {
          window.location.href = redirectUrl.toString();
        }, 1500);
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Sign in failed. Please try again.');
    }
  };

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.9) 0%, rgba(22, 33, 62, 0.9) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      success: '#4CAF50',
      error: '#F44336',
    },
    light: {
      bg: '#f9fafb',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      success: '#4CAF50',
      error: '#dc2626',
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: theme === 'dark'
        ? 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)'
        : 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)',
      padding: '2rem',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: c.cardBg,
        borderRadius: '1.5rem',
        padding: '2.5rem',
        border: `1px solid ${c.cardBorder}`,
        backdropFilter: theme === 'dark' ? 'blur(20px)' : 'none',
        boxShadow: theme === 'dark'
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
      }}>
        {/* Plugin icon/logo */}
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 1.5rem',
          background: theme === 'dark'
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2.5rem',
        }}>
          {status === 'success' ? '✓' : status === 'error' ? '✕' : '🔌'}
        </div>

        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          color: c.text,
          marginBottom: '0.5rem',
        }}>
          {status === 'success' ? 'Authenticated!' :
           status === 'error' ? 'Authentication Failed' :
           `Sign in to ${productName}`}
        </h1>

        <p style={{
          color: c.textMuted,
          fontSize: '0.875rem',
          marginBottom: '2rem',
        }}>
          {status === 'loading' && 'Preparing authentication...'}
          {status === 'authenticating' && 'Sign in to authorize the plugin'}
          {status === 'success' && 'Redirecting you back to the plugin...'}
          {status === 'error' && errorMessage}
        </p>

        {status === 'authenticating' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button
              onClick={handleMockSignIn}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Sign in with Email
            </button>

            <p style={{
              fontSize: '0.75rem',
              color: c.textMuted,
            }}>
              By signing in, you authorize {productName} to access your account
            </p>
          </div>
        )}

        {status === 'success' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            color: c.success,
          }}>
            <span style={{ fontSize: '1.25rem' }}>✓</span>
            <span>Redirecting...</span>
          </div>
        )}

        {status === 'error' && (
          <button
            onClick={() => window.close()}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: c.textMuted,
              border: `1px solid ${c.cardBorder}`,
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              cursor: 'pointer',
            }}
          >
            Close Window
          </button>
        )}

        {/* Loading indicator */}
        {status === 'loading' && (
          <div style={{
            width: '40px',
            height: '40px',
            margin: '0 auto',
            border: `3px solid ${c.cardBorder}`,
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PluginAuthPage;
